/* @img-migrated */
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { Camera, User, Save, Briefcase, FileText, Shield, Lock } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

import Img from '../../components/Img';
import { resolveImg } from '../../lib/images';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PortalSocialProfile = () => {
  const { user, refreshUser } = useAuth();
  const [form, setForm] = useState({ display_name: '', bio: '', title: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingPic, setUploadingPic] = useState(false);
  const [isLeadership, setIsLeadership] = useState(false);
  const [leadershipTitle, setLeadershipTitle] = useState('');

  useEffect(() => { fetchProfile(); }, []);  

  const fetchProfile = async () => {
    try {
      const res = await axios.get(`${API}/portal/social-profile`, { withCredentials: true });
      setForm({ display_name: res.data.display_name || '', bio: res.data.bio || '', title: res.data.title || '' });
      setIsLeadership(res.data.is_leadership || false);
      setLeadershipTitle(res.data.leadership_title || '');
    } catch { toast.error('Failed to load profile'); } finally { setLoading(false); }
  };

  const handleSave = async () => {
    if (!form.display_name.trim()) { toast.error('Display name is required'); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/portal/social-profile`, form, { withCredentials: true });
      toast.success('Social profile updated!');
      refreshUser();
    } catch { toast.error('Failed to update'); } finally { setSaving(false); }
  };

  const handlePicUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingPic(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      await axios.post(`${API}/portal/profile/picture`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Profile photo updated!');
      refreshUser();
    } catch { toast.error('Failed to upload'); } finally { setUploadingPic(false); }
  };

  if (loading) return <div className="flex items-center justify-center min-h-[50vh]"><div className="animate-spin w-8 h-8 border-4 border-[#E63946] border-t-transparent rounded-full" /></div>;

  if (isLeadership) {
    return (
      <div className="min-h-screen bg-[#F0F2F5] py-6 px-4" data-testid="social-profile-page">
        <div className="max-w-2xl mx-auto space-y-5">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-[#050505]">Official Leadership Profile</h1>
            <p className="text-sm text-[#65676B] mt-1">Your profile is managed by the Dominican Chamber of Commerce of Florida.</p>
          </div>

          {/* Locked Profile Notice */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="leadership-locked-notice">
            <div className="bg-gradient-to-r from-[#001630] via-[#002249] to-[#001630] px-6 py-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                  <Shield className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-white font-semibold text-[15px]">Institutional Identity Protected</h3>
                  <p className="text-white/60 text-[12px] mt-0.5">Your visual identity is controlled by the Chamber to maintain institutional standards.</p>
                </div>
              </div>
            </div>
            <div className="p-6 space-y-5">
              {/* Current identity display */}
              <div className="flex items-center gap-4">
                <div className="w-20 h-20 rounded-full overflow-hidden bg-gradient-to-br from-[#E63946] to-[#F4A261] flex items-center justify-center text-white text-xl font-bold shadow-lg flex-shrink-0">
                  {user?.picture ? (
                    <Img variant="avatar" src={resolveImg(user.picture)} alt="" className="w-full h-full object-cover" />
                  ) : (
                    user?.name?.charAt(0).toUpperCase() || '?'
                  )}
                </div>
                <div>
                  <p className="text-[16px] font-bold text-[#050505]">{form.display_name || user?.name}</p>
                  {leadershipTitle && <p className="text-[13px] text-[#E63946] font-medium mt-0.5">{leadershipTitle}</p>}
                  <div className="flex items-center gap-1.5 mt-1.5">
                    <Lock className="w-3 h-3 text-[#65676B]" />
                    <span className="text-[11px] text-[#65676B]">Photo, name, and title managed by Super Admin</span>
                  </div>
                </div>
              </div>

              {/* What's locked */}
              <div className="bg-[#F8F9FA] rounded-lg p-4 space-y-2.5">
                <p className="text-[13px] font-semibold text-[#050505]">What is managed for you:</p>
                <div className="space-y-2">
                  {[
                    'Profile photo',
                    'Profile banner',
                    'Display name',
                    'Official title',
                    'Company / Business profile',
                  ].map((item) => (
                    <div key={item} className="flex items-center gap-2.5 text-[13px] text-[#65676B]">
                      <Lock className="w-3 h-3 flex-shrink-0" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* What they can do */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-[13px] text-green-800 leading-relaxed">
                  <span className="font-semibold">You can still:</span> Post updates, comment, react, send messages, and participate fully in the DCCF community. Only your visual identity is institutionally controlled.
                </p>
              </div>

              <p className="text-[12px] text-[#65676B] text-center">
                Need changes? Contact a <span className="font-semibold text-[#002249]">Super Admin</span> to update your leadership profile.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F0F2F5] py-6 px-4" data-testid="social-profile-page">
      <div className="max-w-2xl mx-auto space-y-5">
        {/* Header */}
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-[#050505]">Personal Social Profile</h1>
          <p className="text-sm text-[#65676B] mt-1">This is your identity inside the DCCF community. Changes here appear in posts, comments, and messages — <span className="font-medium text-[#050505]">not</span> on the public website directory.</p>
        </div>

        {/* Photo */}
        <div className="bg-white rounded-xl shadow-sm p-6" data-testid="social-photo-section">
          <h3 className="text-[15px] font-bold text-[#050505] mb-4 flex items-center gap-2"><Camera className="w-4 h-4 text-[#E63946]" /> Profile Photo</h3>
          <div className="flex items-center gap-5">
            <div className="relative group">
              <div className="w-24 h-24 rounded-full overflow-hidden bg-gradient-to-br from-[#E63946] to-[#F4A261] flex items-center justify-center text-white text-2xl font-bold shadow-lg">
                {user?.picture ? (
                  <Img variant="avatar" src={resolveImg(user.picture)} alt="" className="w-full h-full object-cover" />
                ) : (
                  user?.name?.charAt(0).toUpperCase() || '?'
                )}
              </div>
              <label className="absolute inset-0 rounded-full bg-black/0 group-hover:bg-black/40 flex items-center justify-center cursor-pointer transition-colors">
                <Camera className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                <input type="file" accept="image/*" onChange={handlePicUpload} className="hidden" data-testid="social-photo-upload" />
              </label>
              {uploadingPic && <div className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center"><div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" /></div>}
            </div>
            <div>
              <p className="text-sm font-medium text-[#050505]">Your community photo</p>
              <p className="text-xs text-[#65676B] mt-0.5">Appears in posts, comments, messages, and your social profile.</p>
              <p className="text-xs text-[#65676B] mt-0.5">This does <span className="font-semibold">not</span> change your company logo on the public directory.</p>
            </div>
          </div>
        </div>

        {/* Info */}
        <div className="bg-white rounded-xl shadow-sm p-6" data-testid="social-info-section">
          <h3 className="text-[15px] font-bold text-[#050505] mb-4 flex items-center gap-2"><User className="w-4 h-4 text-[#002249]" /> Profile Information</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#050505] mb-1.5">Display Name <span className="text-[#E63946]">*</span></label>
              <Input value={form.display_name} onChange={e => setForm({ ...form, display_name: e.target.value })}
                placeholder="How you appear in the community" className="h-11 rounded-lg" data-testid="social-display-name" />
              <p className="text-[11px] text-[#65676B] mt-1">Shown on your posts, comments, and messages.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#050505] mb-1.5">Title / Role</label>
              <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. CEO, Marketing Director, Founder" className="h-11 rounded-lg" data-testid="social-title" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#050505] mb-1.5">Personal Bio</label>
              <textarea value={form.bio} onChange={e => setForm({ ...form, bio: e.target.value })}
                placeholder="A short personal introduction for the DCCF community..."
                rows={4} className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm focus:border-[#002249] focus:ring-1 focus:ring-[#002249]/10 outline-none resize-none" data-testid="social-bio" />
              <p className="text-[11px] text-[#65676B] mt-1">Visible on your social profile page.</p>
            </div>
          </div>
        </div>

        {/* Context note */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <p className="text-[13px] text-blue-800 leading-relaxed">
            <span className="font-semibold">Personal vs. Business profile:</span> This page controls your <span className="font-semibold">social/community identity</span> (photo, name, bio). To update your <span className="font-semibold">company information</span> for the public Chamber directory, go to <a href="/portal/profile" className="underline font-semibold">Company Profile</a>.
          </p>
        </div>

        <Button onClick={handleSave} disabled={saving} className="w-full h-12 bg-[#002249] hover:bg-[#001630] rounded-xl text-[15px] font-semibold" data-testid="save-social-profile">
          {saving ? 'Saving...' : 'Save Social Profile'}
          {!saving && <Save className="w-4 h-4 ml-2" />}
        </Button>
      </div>
    </div>
  );
};

export default PortalSocialProfile;
